import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { dashboardApi } from '../../lib/apiClient';
import { fmtBaht, fmtDate } from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';
import { useDateRange } from '../../lib/useDateRange';
import { useSession } from '../../stores/session';
import { TaxInvoiceModal } from './TaxInvoiceModal';

export function ReceiptHistoryView({
  mode = 'history',
}: {
  mode?: 'history' | 'tax-invoice';
}) {
  const isTaxInvoiceMode = mode === 'tax-invoice';
  const { customFromIso, customToIso, setCustom } = useDateRange();
  const { scope } = useBranchScope();
  const userId = useSession((state) => state.user?.id ?? 'anonymous');
  const [from, setFrom] = useState(customFromIso);
  const [to, setTo] = useState(customToIso);
  const [receiptNo, setReceiptNo] = useState('');
  const [selectedReceiptNo, setSelectedReceiptNo] = useState<string | null>(
    null,
  );
  const [taxInvoiceOpen, setTaxInvoiceOpen] = useState(false);

  useEffect(() => {
    setSelectedReceiptNo(null);
    setTaxInvoiceOpen(false);
  }, [isTaxInvoiceMode]);

  const branchId = scope.branchId || undefined;
  const receiptsQuery = useQuery({
    queryKey: [
      'receipts',
      userId,
      from,
      to,
      branchId,
      receiptNo,
      isTaxInvoiceMode,
    ],
    queryFn: () =>
      dashboardApi.listReceipts({
        from,
        to,
        branchId,
        receiptNo: receiptNo.trim() || undefined,
        taxInvoiceIssuedOnly: isTaxInvoiceMode,
        limit: 50,
      }),
  });
  const detailQuery = useQuery({
    queryKey: ['receipt-detail', userId, selectedReceiptNo, branchId, from, to],
    enabled: !!selectedReceiptNo,
    queryFn: () =>
      dashboardApi.getReceipt({
        receiptNo: selectedReceiptNo!,
        branchId,
        from,
        to,
      }),
  });

  const receipts = receiptsQuery.data?.receipts ?? [];
  const selected = detailQuery.data?.receipt;

  return (
    <div className="space-y-4">
      <PageHeader
        title={isTaxInvoiceMode ? 'ใบกำกับภาษี' : 'ประวัติใบเสร็จ'}
        subtitle={
          isTaxInvoiceMode
            ? 'แสดงเฉพาะบิลที่ออกใบกำกับภาษีแล้ว'
            : 'ค้นหา ตรวจสอบ และดูรายละเอียดใบเสร็จย้อนหลัง'
        }
      />

      <Card className="p-4">
        <div className="grid gap-3 md:grid-cols-[160px_160px_1fr_auto]">
          <input
            type="date"
            className="h-10 rounded-btn border border-line bg-surface px-3 text-body"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <input
            type="date"
            className="h-10 rounded-btn border border-line bg-surface px-3 text-body"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          <input
            className="h-10 rounded-btn border border-line bg-surface px-3 text-body"
            placeholder="เลขที่ใบเสร็จ"
            value={receiptNo}
            onChange={(e) => setReceiptNo(e.target.value)}
          />
          <Btn
            onClick={() => {
              setCustom(from, to);
              void receiptsQuery.refetch();
            }}
          >
            ค้นหา
          </Btn>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_420px]">
        <Card className="overflow-hidden">
          {receiptsQuery.isLoading ? (
            <div className="p-6 text-ink-3">กำลังโหลด...</div>
          ) : receipts.length === 0 ? (
            <EmptyState
              title={isTaxInvoiceMode ? 'ไม่พบใบกำกับภาษี' : 'ไม่พบใบเสร็จ'}
              sub={
                isTaxInvoiceMode
                  ? 'ยังไม่มีบิลที่ออกใบกำกับภาษีในช่วงวันที่นี้'
                  : 'ลองเปลี่ยนช่วงวันที่หรือเลขที่ใบเสร็จ'
              }
            />
          ) : (
            <table className="w-full text-left text-body">
              <thead className="bg-bg-warm text-sm-label text-ink-3">
                <tr>
                  <th className="p-3">เลขที่</th>
                  <th className="p-3">วันที่</th>
                  <th className="p-3">สาขา</th>
                  <th className="p-3">โปรโมชัน</th>
                  <th className="p-3 text-right">ยอดรวม</th>
                  <th className="p-3 text-right">
                    {isTaxInvoiceMode ? 'ออกเมื่อ' : 'สำเนา'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {receipts.map((receipt) => (
                  <tr
                    key={receipt.saleId}
                    className="cursor-pointer border-t border-line hover:bg-bg-warm"
                    onClick={() => {
                      setSelectedReceiptNo(receipt.receiptNo);
                      setTaxInvoiceOpen(false);
                    }}
                  >
                    <td className="p-3 font-semibold tabnum">
                      {receipt.receiptNo}
                    </td>
                    <td className="p-3">{fmtDate(new Date(receipt.soldAt))}</td>
                    <td className="p-3">{receipt.branchId}</td>
                    <td className="p-3">
                      {receipt.appliedPromotions.length > 0 ? (
                        <span className="rounded-full bg-status-success/10 px-2 py-1 text-sm-label text-status-success">
                          {receipt.appliedPromotions.length} รายการ
                        </span>
                      ) : (
                        <span className="text-ink-3">—</span>
                      )}
                    </td>
                    <td className="p-3 text-right tabnum">
                      {fmtBaht(receipt.totalAmount)}
                    </td>
                    <td className="p-3 text-right tabnum">
                      {isTaxInvoiceMode && receipt.taxInvoiceIssuedAt
                        ? fmtDate(new Date(receipt.taxInvoiceIssuedAt))
                        : receipt.copyCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card className="p-4">
          {!selectedReceiptNo ? (
            <EmptyState
              title="เลือกใบเสร็จ"
              sub={
                isTaxInvoiceMode
                  ? 'เลือกรายการเพื่อดูหรือพิมพ์ใบกำกับภาษีอีกครั้ง'
                  : 'รายละเอียดจะแสดงด้านนี้'
              }
            />
          ) : detailQuery.isLoading ? (
            <div className="text-ink-3">กำลังโหลดรายละเอียด...</div>
          ) : !selected ? (
            <EmptyState title="ไม่พบรายละเอียด" />
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-sm-label text-ink-3">ใบเสร็จ</div>
                <div className="text-h3 font-bold tabnum">
                  {selected.receiptNo}
                </div>
                <div className="text-body text-ink-3">
                  {fmtDate(new Date(selected.soldAt))} · {selected.branchId}
                </div>
              </div>
              <div className="divide-y divide-line">
                {selected.lines.map((line) => (
                  <div
                    key={line.id}
                    className="flex items-start justify-between gap-3 py-2"
                  >
                    <div>
                      <div className="font-semibold">
                        {line.productName ?? line.productId}
                      </div>
                      <div className="text-sm-label text-ink-3 tabnum">
                        {line.quantity} × {fmtBaht(line.unitPrice)}
                      </div>
                    </div>
                    <div className="tabnum">{fmtBaht(line.amount)}</div>
                  </div>
                ))}
              </div>
              {selected.appliedPromotions.length > 0 && (
                <div className="rounded-card bg-status-success/5 p-3">
                  <div className="mb-2 font-semibold text-status-success">
                    โปรโมชันที่ใช้
                  </div>
                  <div className="space-y-2">
                    {selected.appliedPromotions.map((promotion) => (
                      <div
                        key={promotion.id}
                        className="flex items-start justify-between gap-3 text-body"
                      >
                        <div>
                          <div>{promotion.name}</div>
                          {promotion.detail && (
                            <div className="text-sm-label text-ink-3">
                              {promotion.detail}
                            </div>
                          )}
                          {promotion.freeQuantity !== undefined &&
                            promotion.freeQuantity > 0 && (
                              <div className="text-sm-label text-ink-3">
                                ของแถม {promotion.freeQuantity} ชิ้น
                              </div>
                            )}
                        </div>
                        <div className="shrink-0 tabnum text-status-success">
                          -{fmtBaht(promotion.discountAmount)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(selected.billDiscount ?? 0) > 0 && (
                <div className="flex items-center justify-between rounded-card bg-warn-light p-3 text-body text-warn">
                  <span className="font-semibold">ส่วนลดท้ายบิล</span>
                  <span className="tabnum">
                    -{fmtBaht(selected.billDiscount ?? 0)}
                  </span>
                </div>
              )}
              {(selected.roundingDelta ?? 0) !== 0 && (
                <div className="flex items-center justify-between rounded-card bg-bg-warm p-3 text-body text-ink">
                  <span className="font-semibold">ยอดปัดเศษ</span>
                  <span className="tabnum">
                    {(selected.roundingDelta ?? 0) > 0 ? '+' : ''}
                    {fmtBaht(selected.roundingDelta ?? 0)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between border-t border-line pt-3 text-h3 font-bold">
                <span>รวม</span>
                <span className="tabnum">{fmtBaht(selected.totalAmount)}</span>
              </div>
              <div className="text-sm-label text-ink-3">
                ชำระเงิน:{' '}
                {selected.payments
                  .map(
                    (payment) => payment.paymentLabel?.trim() || payment.method,
                  )
                  .join(', ') || '—'}
              </div>
              <Btn kind="primary" onClick={() => setTaxInvoiceOpen(true)}>
                {isTaxInvoiceMode
                  ? 'พิมพ์ใบกำกับภาษีอีกครั้ง'
                  : 'ออกใบกำกับภาษี'}
              </Btn>
              <TaxInvoiceModal
                open={taxInvoiceOpen}
                receipt={selected}
                onClose={() => setTaxInvoiceOpen(false)}
                onIssued={() => {
                  void receiptsQuery.refetch();
                  void detailQuery.refetch();
                }}
              />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
